import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuthToken } from "../../../utils/getAuthToken";

// Async thunk to get a role by ID
export const getRoleById = createAsyncThunk(
  "updateFunctions/getRoleById",
  async (_id, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue({ message: "No token found for authentication" });
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/roles/rolebyid/${_id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue({
          message:
            result.message || "An error occurred while fetching the role.",
        });
      }

      return result;
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Failed to fetch role",
      });
    }
  }
);

// Async thunk to change roles by ID
export const changeRoleById = createAsyncThunk(
  "updateFunctions/changeRoleById",
  async ({ id, roleData }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue({ message: "No token found for authentication" });
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/roles/changeroles/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(roleData),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue({
          message:
            result.message || "An error occurred while updating the role.",
        });
      }

      return result;
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Failed to update role",
      });
    }
  }
);

// Async thunk to update a team member by ID
export const updateTeamMemberById = createAsyncThunk(
  "updateFunctions/updateTeamMemberById",
  async ({ id, teamMemberData }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue({ message: "No token found for authentication" });
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/teammembers/update/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(teamMemberData),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue({
          message:
            result.message ||
            "An error occurred while updating the team member.",
        });
      }

      return result;
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Failed to update team member",
      });
    }
  }
);

const updateFunctionsSlice = createSlice({
  name: "updateFunctions",
  initialState: {
    loading: false,
    error: null,
    successMessage: null,
    role: null, // To store the fetched role object
  },
  reducers: {
    clearSuccessMessage(state) {
      state.successMessage = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // getRoleById cases
      .addCase(getRoleById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(getRoleById.fulfilled, (state, action) => {
        state.loading = false;
        state.role = action.payload;
        state.successMessage = "Role fetched successfully";
      })
      .addCase(getRoleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })

      // changeRoleById cases
      .addCase(changeRoleById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(changeRoleById.fulfilled, (state, action) => {
        state.loading = false;
        state.role = action.payload;
        state.successMessage = "Role updated successfully";
      })
      .addCase(changeRoleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })

      // updateTeamMemberById cases
      .addCase(updateTeamMemberById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateTeamMemberById.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Team member updated successfully";
        // Optionally update state if necessary
      })
      .addCase(updateTeamMemberById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      });
  },
});

export const { clearSuccessMessage } = updateFunctionsSlice.actions;

export default updateFunctionsSlice.reducer;
